// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Typography, Checkbox, Button } from '@material-ui/core';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';

const FeatureControl = ({ match }) => {
  const pageId = match.params.id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [controls, setControls] = useState({
    mapBound:       false,
    pageHighlights: false,
    dealMap:        false,
    foodReviews:    false,
  });

  const changeControlsHandler = (checked, key) => {
    setControls((prev) => ({ ...prev, [key]: checked }));
  };

  const getFeatureControl = async () => {
    try {
      setLoading(true);
      const featureControl = await apiService.getPageFeature(pageId);

      if (!_.isEmpty(featureControl)) {
        const { mapBound, pageHighlights, dealMap, foodReviews }  = featureControl;

        setControls({
          mapBound:       mapBound || false,
          pageHighlights: pageHighlights || false,
          dealMap:        dealMap || false,
          foodReviews:    foodReviews || false,
        });
      }

      setData(featureControl);
    } catch (error) {
      toast.error('Error while fetching data for feature control');
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const saveControlsHandler = async () => {
    try {
      setLoading(true);

      if (_.isEmpty(data)) { // create
        await apiService.createPageFeature(pageId, controls);
        toast.success('Feature control created successfully');
      } else { // update
        await apiService.updatePageFeature(pageId, controls);
        toast.success('Feature control updated successfully');
      }

      // get latest data
      await getFeatureControl();
    } catch (err) {
      toast.error(`Unable to ${_.isEmpty(data) ? 'add' : 'update'} feature contols, please report to IT team.`);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFeatureControl();
  }, []);

  return (
    <>
      {loading && <OverLoading />}
      <div className="container">
        <div className="w-100 mb-3">
          <Typography variant="h6" color="inherit" className="">
            Feature Control
          </Typography>
          <Typography variant="subtitle1" color="inherit" className="">
            Toggle checkbox to enable or disable page feature
          </Typography>
        </div>

        <div className="mt-4">
          <MCheckbox
            labelText="Mapbound"
            subLabelText="Enable display of callout title & description and map boundary in both Deal and Food Review maps; Enable searching of deals within map boundary in Deal map."
            checked={controls.mapBound}
            onChange={(e) => changeControlsHandler(e.target.checked, 'mapBound')}
          />

          <MCheckbox
            labelText="Page Highlights"
            subLabelText="Enable location based display of special and exclusive highlights on the home screen; Enable display of page highlights, food reviews and posts grouped by categories under map boundary in page screen."
            checked={controls.pageHighlights}
            onChange={(e) => changeControlsHandler(e.target.checked, 'pageHighlights')}
          />

          <MCheckbox
            labelText="Deal Map"
            subLabelText="Enable page to be displaed under search suggestion in Deal map"
            checked={controls.dealMap}
            onChange={(e) => changeControlsHandler(e.target.checked, 'dealMap')}
          />

          <MCheckbox
            labelText="Food Reviews"
            subLabelText="Enable page to be displayed under search suggestion in Food Reviews map"
            checked={controls.foodReviews}
            onChange={(e) => changeControlsHandler(e.target.checked, 'foodReviews')}
          />
        </div>

        <div className="mt-5">
          <Button color="primary" variant="contained" onClick={saveControlsHandler}>
            { _.isEmpty(data) ? 'Create' : 'Update' }
          </Button>
        </div>
      </div>
    </>
  );
};

export default FeatureControl;

FeatureControl.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

const MCheckbox = ({ labelText, subLabelText, checked, onChange }) => (
  <div className="mt-2">
    <div className="d-flex align-items-start">
      <Checkbox checked={checked} onChange={onChange} />
      <div className="ml-4 mt-2">
        <Typography variant="subtitle1" color="inherit" className="font-weight-bold">{labelText}</Typography>
        <Typography variant="body1" style={{ fontSize: '11px', color: Colours.Gray1 }}>{subLabelText}</Typography>
      </div>
    </div>
  </div>
);

MCheckbox.propTypes = {
  labelText:    PropTypes.string.isRequired,
  subLabelText: PropTypes.string.isRequired,
  checked:      PropTypes.bool.isRequired,
  onChange:     PropTypes.func.isRequired,
};
